import React, { useEffect, useMemo, useState } from "react";
import {
  adminBroadcastFiltersMeta,
  adminBroadcastRecipientsCount,
  adminBroadcastRecipientsPreview,
  adminBroadcastSend,
} from "../utils/adminApi";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import { GhostButton, PrimaryButton } from "../../design-system";

const useMatchMedia = (query, defaultValue = false) => {
  const [matches, setMatches] = useState(() => {
    try {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return !!defaultValue;
      return !!window.matchMedia(query).matches;
    } catch {
      return !!defaultValue;
    }
  });

  useEffect(() => {
    try {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
      const mql = window.matchMedia(query);

      const handler = (e) => setMatches(!!e.matches);
      // Ensure state is in sync on mount (query may have changed).
      setMatches(!!mql.matches);

      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
      }

      // Safari / older browsers.
      if (typeof mql.addListener === "function") {
        mql.addListener(handler);
        return () => mql.removeListener(handler);
      }
    } catch {
      // ignore
    }
    return undefined;
  }, [query]);

  return matches;
};

const parseOptionalInt = (v, { min = 0, max = 999999 } = {}) => {
  // IMPORTANT: treat empty input as "unset" (null), not 0.
  const s = String(v ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  if (i < min) return min;
  if (i > max) return max;
  return i;
};

const buildPayload = (filters, q) => {
  const payload = { filters: { ...filters } };
  if (q && q.trim()) payload.q = q.trim();
  return payload;
};

const DELIVERY_CHOICES = [
  { value: "inbox", label: "Inbox" },
  { value: "email", label: "Email" },
  { value: "both", label: "Both" },
];

const EMAIL_TEMPLATE_VARIANTS = [
  { value: "default", label: "Default (stub)" },
  { value: "marketing", label: "Marketing (stub)" },
  { value: "product_update", label: "Product update (stub)" },
];

const BroadcastsAdminPage = () => {
  const isMobile = useMatchMedia("(max-width: 767px)", false);
  const [filtersMeta, setFiltersMeta] = useState(null);
  const [filtersMetaError, setFiltersMetaError] = useState(null);

  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    email_verified: null, // true|false|null
    marketing_opt_in: null, // true|false|null
    plan_name: "", // "", "free"|"plus"|"pro"
    will_renew: null, // true|false|null
    is_trialing: null, // true|false|null
    trial_days_min: "", // string input
    trial_days_max: "", // string input
  });

  const payload = useMemo(() => {
    const out = {};
    if (filters.email_verified !== null) out.email_verified = filters.email_verified;
    if (filters.marketing_opt_in !== null) out.marketing_opt_in = filters.marketing_opt_in;
    if (filters.plan_name) out.plan_name = filters.plan_name;
    if (filters.will_renew !== null) out.will_renew = filters.will_renew;
    if (filters.is_trialing !== null) out.is_trialing = filters.is_trialing;

    const min = parseOptionalInt(filters.trial_days_min, { min: 0, max: 3650 });
    const max = parseOptionalInt(filters.trial_days_max, { min: 0, max: 3650 });
    if (min !== null || max !== null) {
      out.trial_days_remaining = {};
      if (min !== null) out.trial_days_remaining.min = min;
      if (max !== null) out.trial_days_remaining.max = max;
    }

    return buildPayload(out, q);
  }, [filters, q]);

  const [totalCount, setTotalCount] = useState(null);
  const [sample, setSample] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Selection model
  const [selectionMode, setSelectionMode] = useState("manual"); // "manual" | "all_matching"
  const [includedUserIds, setIncludedUserIds] = useState(() => new Set());
  const [excludedUserIds, setExcludedUserIds] = useState(() => new Set());

  const computedWillSendTo = useMemo(() => {
    if (selectionMode === "manual") return includedUserIds.size;
    if (totalCount == null) return null;
    return Math.max(0, totalCount - excludedUserIds.size);
  }, [selectionMode, includedUserIds, excludedUserIds, totalCount]);

  const isUserSelected = (userId) => {
    if (selectionMode === "manual") return includedUserIds.has(userId);
    return !excludedUserIds.has(userId);
  };

  const toggleRowSelection = (userId) => {
    if (selectionMode === "manual") {
      setIncludedUserIds((prev) => {
        const next = new Set(prev);
        if (next.has(userId)) next.delete(userId);
        else next.add(userId);
        return next;
      });
      return;
    }
    // all_matching: unchecking excludes
    setExcludedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectAllMatching = () => {
    setSelectionMode("all_matching");
    setIncludedUserIds(new Set());
    setExcludedUserIds(new Set());
  };

  const clearSelection = () => {
    setSelectionMode("manual");
    setIncludedUserIds(new Set());
    setExcludedUserIds(new Set());
  };

  // Composer + delivery (UI only)
  const [title, setTitle] = useState("");
  const [bodyMarkdown, setBodyMarkdown] = useState("");
  const [delivery, setDelivery] = useState("inbox");
  const [emailVariant, setEmailVariant] = useState("default");

  // Confirmation modal (UI only)
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'info'|'success'|'error', message: string }

  useEffect(() => {
    let mounted = true;
    adminBroadcastFiltersMeta()
      .then((data) => {
        if (!mounted) return;
        setFiltersMeta(data);
        setFiltersMetaError(null);
      })
      .catch((e) => {
        if (!mounted) return;
        setFiltersMetaError(e?.message || "Failed to load filter metadata");
      });
    return () => {
      mounted = false;
    };
  }, []);

  const sanitizeSampleUsers = (users) => {
    // Defensive: backend should exclude this, but keep UI safe.
    const blockedEmails = new Set(["template-preview-system@myvitae.ai"]);
    const blockedUsernames = new Set(["template-preview-system"]);
    return (users || []).filter((u) => {
      const email = String(u?.email || "").toLowerCase().trim();
      const username = String(u?.username || "").toLowerCase().trim();
      if (blockedEmails.has(email)) return false;
      if (blockedUsernames.has(username)) return false;
      return true;
    });
  };

  useEffect(() => {
    let mounted = true;
    const t = setTimeout(async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [countResp, previewResp] = await Promise.all([
          adminBroadcastRecipientsCount(payload),
          adminBroadcastRecipientsPreview({ ...payload, limit: 30 }),
        ]);
        if (!mounted) return;
        setTotalCount(countResp?.total_count ?? 0);
        setSample(sanitizeSampleUsers(previewResp?.sample || []));
      } catch (e) {
        if (!mounted) return;
        setLoadError(e?.message || "Failed to load recipients");
        setTotalCount(null);
        setSample([]);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }, 250); // debounce

    return () => {
      mounted = false;
      clearTimeout(t);
    };
  }, [payload]);

  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_e) {
      // ignore
    }
    // Fallback
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.top = "-1000px";
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch (_e) {
      return false;
    }
  };

  const excerpt = useMemo(() => {
    const t = (bodyMarkdown || "").trim();
    if (!t) return "";
    return t.length > 140 ? `${t.slice(0, 140)}…` : t;
  }, [bodyMarkdown]);

  const confirmDisabled = computedWillSendTo === 0 || !title.trim() || !bodyMarkdown.trim();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {notice && (
        <div
          className={`mb-4 rounded-2xl border px-4 py-3 ${
            notice.type === "error"
              ? "border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
              : notice.type === "success"
              ? "border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
              : "border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <p className={`${typography.bodySmall}`}>{notice.message}</p>
            <GhostButton onClick={() => setNotice(null)} size="sm">
              Dismiss
            </GhostButton>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {/* 1: broadcast message specification */}
        <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
          <h2 className={`${typography.h4} ${textColors.primary} mb-3`}>Broadcast</h2>

          <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Title</label>
          <input
            className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} ${textColors.primary} px-3 py-2 mb-3`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Message title"
          />

          <div className="flex items-center justify-between mb-1">
            <label className={`${typography.bodySmall} ${textColors.secondary}`}>Body (Markdown)</label>
          </div>
          <textarea
            className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} ${textColors.primary} px-3 py-2 mb-3 min-h-[160px]`}
            value={bodyMarkdown}
            onChange={(e) => setBodyMarkdown(e.target.value)}
            placeholder="Write your message (Markdown supported, e.g. **bold**)."
          />

          <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Delivery</label>
          <select
            className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} ${textColors.primary} px-3 py-2 mb-3`}
            value={delivery}
            onChange={(e) => setDelivery(e.target.value)}
          >
            {DELIVERY_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>

          {(delivery === "email" || delivery === "both") && (
            <>
              <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Email template</label>
              <select
                className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glassCard} ${textColors.primary} px-3 py-2 mb-3`}
                value={emailVariant}
                onChange={(e) => setEmailVariant(e.target.value)}
              >
                {EMAIL_TEMPLATE_VARIANTS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* 2: recipients */}
        <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
          <div className={`flex ${isMobile ? "flex-col" : "items-start justify-between"} gap-4 mb-4`}>
            <div>
              <h2 className={`${typography.h4} ${textColors.primary}`}>Recipients</h2>
              <div className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
                Matches: <span className={`${textColors.primary} font-semibold`}>{totalCount ?? "—"}</span> · Showing a
                sample of <span className={`${textColors.primary} font-semibold`}>30</span>
              </div>
            </div>
            <div className={`flex ${isMobile ? "flex-col w-full" : "items-center"} gap-2`}>
              <GhostButton onClick={selectAllMatching} size="sm" disabled={totalCount === 0} className={isMobile ? "w-full" : ""}>
                Select all matching{totalCount != null ? ` (${totalCount})` : ""}
              </GhostButton>
              <GhostButton onClick={clearSelection} size="sm" className={isMobile ? "w-full" : ""}>
                Clear selection
              </GhostButton>
            </div>
          </div>

          {/* Filters */}
          <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-3 mb-4`}>
            <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3"} gap-3`}>
              <div>
                <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Search</label>
                <input
                  className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glass} ${textColors.primary} px-3 py-2`}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="username or email"
                />
              </div>

              <div>
                <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Email verified</label>
                <select
                  className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glass} ${textColors.primary} px-3 py-2`}
                  value={filters.email_verified === null ? "any" : filters.email_verified ? "yes" : "no"}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      email_verified: e.target.value === "any" ? null : e.target.value === "yes",
                    }))
                  }
                >
                  <option value="any">Any</option>
                  <option value="yes">Verified</option>
                  <option value="no">Not verified</option>
                </select>
              </div>

              <div>
                <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Marketing opt-in</label>
                <select
                  className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glass} ${textColors.primary} px-3 py-2`}
                  value={filters.marketing_opt_in === null ? "any" : filters.marketing_opt_in ? "yes" : "no"}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      marketing_opt_in: e.target.value === "any" ? null : e.target.value === "yes",
                    }))
                  }
                >
                  <option value="any">Any</option>
                  <option value="yes">Opted in</option>
                  <option value="no">Not opted in</option>
                </select>
              </div>

              <div>
                <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Plan</label>
                <select
                  className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glass} ${textColors.primary} px-3 py-2`}
                  value={filters.plan_name}
                  onChange={(e) => setFilters((p) => ({ ...p, plan_name: e.target.value }))}
                >
                  <option value="">Any</option>
                  {(filtersMeta?.plan_names || ["free", "plus", "pro"]).map((pn) => (
                    <option key={pn} value={pn}>
                      {pn}
                    </option>
                  ))}
                </select>
                {filtersMetaError && (
                  <div className={`${typography.bodySmall} text-red-500 mt-1`}>{filtersMetaError}</div>
                )}
              </div>

              <div>
                <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Will renew</label>
                <select
                  className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glass} ${textColors.primary} px-3 py-2`}
                  value={filters.will_renew === null ? "any" : filters.will_renew ? "yes" : "no"}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      will_renew: e.target.value === "any" ? null : e.target.value === "yes",
                    }))
                  }
                >
                  <option value="any">Any</option>
                  <option value="yes">Renewing</option>
                  <option value="no">Not renewing</option>
                </select>
              </div>

              <div>
                <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>Trialing</label>
                <select
                  className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glass} ${textColors.primary} px-3 py-2`}
                  value={filters.is_trialing === null ? "any" : filters.is_trialing ? "yes" : "no"}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      is_trialing: e.target.value === "any" ? null : e.target.value === "yes",
                    }))
                  }
                >
                  <option value="any">Any</option>
                  <option value="yes">Trialing</option>
                  <option value="no">Not trialing</option>
                </select>
              </div>

              <div>
                <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>
                  Trial days remaining (min)
                </label>
                <input
                  className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glass} ${textColors.primary} px-3 py-2`}
                  value={filters.trial_days_min}
                  onChange={(e) => setFilters((p) => ({ ...p, trial_days_min: e.target.value }))}
                  placeholder="e.g. 3"
                />
              </div>
              <div>
                <label className={`${typography.bodySmall} ${textColors.secondary} block mb-1`}>
                  Trial days remaining (max)
                </label>
                <input
                  className={`w-full rounded-2xl border ${borders.glass} ${surfaces.glass} ${textColors.primary} px-3 py-2`}
                  value={filters.trial_days_max}
                  onChange={(e) => setFilters((p) => ({ ...p, trial_days_max: e.target.value }))}
                  placeholder="e.g. 14"
                />
              </div>
            </div>
          </div>

          {/* Table (Desktop) / Cards (Mobile) */}
          {isMobile ? (
            <div className="space-y-3">
              {loading && (
                <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
                  <span className={`${typography.bodySmall} ${textColors.secondary}`}>Loading…</span>
                </div>
              )}
              {!loading && loadError && (
                <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
                  <span className={`${typography.bodySmall} text-red-500`}>{loadError}</span>
                </div>
              )}
              {!loading && !loadError && sample.length === 0 && (
                <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}>
                  <span className={`${typography.bodySmall} ${textColors.secondary}`}>No matches.</span>
                </div>
              )}
              {!loading &&
                !loadError &&
                sample.map((u) => {
                  const sub = u.subscription || {};
                  const trialText =
                    sub.status === "trialing"
                      ? `Trialing (${sub.trial_days_remaining ?? "?"}d)`
                      : sub.status || "—";
                  return (
                    <div
                      key={u.id}
                      className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} p-4`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={isUserSelected(u.id)}
                            onChange={() => toggleRowSelection(u.id)}
                            className="mt-1 shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <div className={`${typography.body} ${textColors.primary} font-medium break-words`}>
                              {u.username}
                            </div>
                            <div className={`${typography.bodySmall} ${textColors.secondary} break-all mt-1`}>
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className={`grid grid-cols-2 gap-2 mt-3 pt-3 border-t ${borders.glass}`}>
                        <div>
                          <div className={`${typography.caption} ${textColors.secondary}`}>Verified</div>
                          <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5`}>
                            {u.email_verified ? "Yes" : "No"}
                          </div>
                        </div>
                        <div>
                          <div className={`${typography.caption} ${textColors.secondary}`}>Marketing</div>
                          <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5`}>
                            {u.marketing_opt_in ? "Yes" : "No"}
                          </div>
                        </div>
                        <div>
                          <div className={`${typography.caption} ${textColors.secondary}`}>Plan</div>
                          <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5`}>
                            {sub.plan_name || "—"}
                          </div>
                        </div>
                        <div>
                          <div className={`${typography.caption} ${textColors.secondary}`}>Will renew</div>
                          <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5`}>
                            {sub.will_renew ? "Yes" : "No"}
                          </div>
                        </div>
                        <div className="col-span-2">
                          <div className={`${typography.caption} ${textColors.secondary}`}>Trial</div>
                          <div className={`${typography.bodySmall} ${textColors.primary} mt-0.5`}>{trialText}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className={`rounded-2xl border ${borders.glass} ${surfaces.glassCard} overflow-hidden`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={`${surfaces.glass} border-b ${borders.glass}`}>
                    <tr className={`${textColors.secondary}`}>
                      <th className="px-3 py-2 text-left">Select</th>
                      <th className="px-3 py-2 text-left">Username</th>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Verified</th>
                      <th className="px-3 py-2 text-left">Marketing</th>
                      <th className="px-3 py-2 text-left">Plan</th>
                      <th className="px-3 py-2 text-left">Will renew</th>
                      <th className="px-3 py-2 text-left">Trial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td className="px-3 py-4" colSpan={8}>
                          <span className={`${typography.bodySmall} ${textColors.secondary}`}>Loading…</span>
                        </td>
                      </tr>
                    )}
                    {!loading && loadError && (
                      <tr>
                        <td className="px-3 py-4" colSpan={8}>
                          <span className={`${typography.bodySmall} text-red-500`}>{loadError}</span>
                        </td>
                      </tr>
                    )}
                    {!loading && !loadError && sample.length === 0 && (
                      <tr>
                        <td className="px-3 py-4" colSpan={8}>
                          <span className={`${typography.bodySmall} ${textColors.secondary}`}>No matches.</span>
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      !loadError &&
                      sample.map((u) => {
                        const sub = u.subscription || {};
                        const trialText =
                          sub.status === "trialing"
                            ? `Trialing (${sub.trial_days_remaining ?? "?"}d)`
                            : sub.status || "—";
                        return (
                          <tr key={u.id} className={`border-t ${borders.glass}`}>
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={isUserSelected(u.id)}
                                onChange={() => toggleRowSelection(u.id)}
                              />
                            </td>
                            <td className={`px-3 py-2 ${textColors.primary}`}>{u.username}</td>
                            <td className={`px-3 py-2 ${textColors.primary}`}>{u.email}</td>
                            <td className={`px-3 py-2 ${textColors.primary}`}>{u.email_verified ? "Yes" : "No"}</td>
                            <td className={`px-3 py-2 ${textColors.primary}`}>{u.marketing_opt_in ? "Yes" : "No"}</td>
                            <td className={`px-3 py-2 ${textColors.primary}`}>{sub.plan_name || "—"}</td>
                            <td className={`px-3 py-2 ${textColors.primary}`}>{sub.will_renew ? "Yes" : "No"}</td>
                            <td className={`px-3 py-2 ${textColors.primary}`}>{trialText}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* 3: review & confirm */}
        <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-4`}>
          <h2 className={`${typography.h4} ${textColors.primary} mb-3`}>Review & Confirm</h2>
          <div className={`flex ${isMobile ? "flex-col" : "items-center justify-between"} gap-2`}>
            <div className={`${typography.bodySmall} ${textColors.secondary}`}>
              Will send to:{" "}
              <span className={`${textColors.primary} font-semibold`}>
                {computedWillSendTo == null ? "—" : computedWillSendTo}
              </span>
            </div>
            <PrimaryButton
              onClick={() => setShowConfirm(true)}
              disabled={confirmDisabled}
              size="sm"
              className={isMobile ? "w-full" : ""}
            >
              Review & Confirm
            </PrimaryButton>
          </div>
          {confirmDisabled && (
            <div className={`${typography.bodySmall} ${textColors.secondary} mt-2`}>
              Add title + body, and select at least 1 recipient.
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal (stub send) */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowConfirm(false)} />
          <div className={`relative w-full max-w-lg rounded-2xl border ${borders.glass} ${surfaces.glassCard} ${isMobile ? "p-4" : "p-6"} max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className={`${typography.h4} ${textColors.primary}`}>Confirm broadcast</div>
                <div className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
                  This will send the broadcast now. Delivery is chunked to avoid timeouts.
                </div>
              </div>
              <GhostButton onClick={() => setShowConfirm(false)} size="sm">
                Close
              </GhostButton>
            </div>

            <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-3 mb-3`}>
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Recipients</div>
              <div className={`${typography.body} ${textColors.primary} font-semibold`}>
                {computedWillSendTo == null ? "—" : `${computedWillSendTo} user(s)`}
              </div>
              <div className={`${typography.bodySmall} ${textColors.secondary} mt-2`}>Channels</div>
              <div className={`${typography.body} ${textColors.primary} font-semibold`}>
                {delivery}
                {(delivery === "email" || delivery === "both") && ` · template=${emailVariant}`}
              </div>
              <div className={`${typography.bodySmall} ${textColors.secondary} mt-2`}>Title</div>
              <div className={`${typography.body} ${textColors.primary} font-semibold`}>{title || "—"}</div>
              <div className={`${typography.bodySmall} ${textColors.secondary} mt-2`}>Message preview</div>
              <div className={`${typography.bodySmall} ${textColors.primary}`}>{excerpt || "—"}</div>
              <div className={`${typography.caption} ${textColors.secondary} mt-1`}>
                Preview shows the first ~140 characters of the message body.
              </div>
              <div className={`${typography.bodySmall} text-yellow-600 mt-3`}>
                Warning: broadcast send is intended to be chunked and irreversible once implemented.
              </div>
            </div>

            <div className={`flex ${isMobile ? "flex-col" : "items-center justify-end"} gap-2`}>
              <GhostButton onClick={() => setShowConfirm(false)} className={isMobile ? "w-full" : ""}>
                Cancel
              </GhostButton>
              <GhostButton
                onClick={async () => {
                  const stubPayload = {
                    ...payload,
                    selection: {
                      mode: selectionMode,
                      includedUserIds: Array.from(includedUserIds),
                      excludedUserIds: Array.from(excludedUserIds),
                    },
                    message: { title, body_markdown: bodyMarkdown },
                    delivery: { channel: delivery, email_variant: emailVariant },
                  };
                  const ok = await copyToClipboard(JSON.stringify(stubPayload, null, 2));
                  setNotice({
                    type: ok ? "success" : "info",
                    message: ok ? "Copied send payload to clipboard (Roadmap 3 will implement the actual send)." : "Send payload prepared (copy not supported).",
                  });
                }}
                className={isMobile ? "w-full" : ""}
              >
                Copy payload
              </GhostButton>
              <PrimaryButton
                disabled={confirmDisabled}
                onClick={async () => {
                  setIsSending(true);
                  try {
                    // Convert UI payload to Roadmap 3 backend contract
                    const channels =
                      delivery === "both"
                        ? { inbox: true, email: true }
                        : delivery === "email"
                        ? { inbox: false, email: true }
                        : { inbox: true, email: false };

                    const sendPayload = {
                      filters: payload.filters || {},
                      q: payload.q,
                      selection_mode: selectionMode,
                      included_user_ids: Array.from(includedUserIds),
                      excluded_user_ids: Array.from(excludedUserIds),
                      channels,
                      title,
                      body_markdown: bodyMarkdown,
                      email_template_variant: emailVariant,
                      dry_run: false,
                    };

                    const res = await adminBroadcastSend(sendPayload);
                    setNotice({
                      type: "success",
                      message: `Broadcast sent to ${res?.total_recipients ?? "?"} user(s). Inbox=${res?.sent_counts?.inbox ?? 0}, Email=${res?.sent_counts?.email ?? 0}.`,
                    });
                    setShowConfirm(false);
                  } catch (e) {
                    setNotice({ type: "error", message: e?.message || "Failed to send broadcast." });
                  } finally {
                    setIsSending(false);
                  }
                }}
                className={isMobile ? "w-full" : ""}
              >
                {isSending ? "Sending…" : "Confirm send"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastsAdminPage;

